import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';

import { catchError, forkJoin, map, Observable, of } from 'rxjs';

import { Address } from '../models/address.model';

import { AccountService } from '../services/account.service';
import { AddressService } from '../services/address.service';
import { AreasService } from '../services/areas.service';
import { NavigationService } from '../services/navigation.service';
import { PlatformService } from '../services/ssr/platform.service';
import { VendorsService } from '../services/vendors.service';

@Injectable({
  providedIn: 'root',
})
export class HomePageGuard implements CanActivate {
  constructor(
    private router: Router,
    private areasService: AreasService,
    private addressService: AddressService,
    private accountService: AccountService,
    private vendorsService: VendorsService,
    private platformService: PlatformService,
    private navigationService: NavigationService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot) {
    if (!this.platformService.isCordova) {
      void this.navigationService.navigateTo('/vendors/search');
      return false;
    }

    const currentQueryParams = route.queryParams || {};
    const urlSegments = [this.navigationService.getCountryLanguageUrlSegment(), 'vendors', 'home'];

    return this.checkAreaId(route).pipe(
      map((areaCheckResult) => {
        const { shouldUpdateUrl: shouldUpdateAreaIdFromUrl, newValue: newAreaId } = areaCheckResult;

        if (shouldUpdateAreaIdFromUrl) {
          return this.router.createUrlTree(urlSegments, {
            fragment: undefined,
            queryParams: { ...currentQueryParams, areaId: newAreaId },
          });
        }

        return true;
      }),
    );
  }

  private checkAreaId(route: ActivatedRouteSnapshot): Observable<{ shouldUpdateUrl: boolean; newValue?: number }> {
    const areaIdFromUrl = this.navigationService.getAreaIdFromUrl(route);
    const addressFromSearchFilters = this.vendorsService.getAddressSearchFilters();

    if (!areaIdFromUrl) {
      return of({
        shouldUpdateUrl: !!addressFromSearchFilters,
        newValue: addressFromSearchFilters ? addressFromSearchFilters.area.areaId : undefined,
      });
    }

    if (addressFromSearchFilters && addressFromSearchFilters.area.areaId === areaIdFromUrl) {
      return of({ shouldUpdateUrl: false });
    }

    return forkJoin([
      this.areasService.getAreaById(areaIdFromUrl).pipe(catchError(() => of(undefined))),
      this.accountService.isLoggedIn()
        ? this.accountService.getUserSavedAddresses(true).pipe(catchError(() => of([] as Array<Address>)))
        : of([] as Array<Address>),
    ]).pipe(
      map(([area, savedAddresses]) => {
        if (savedAddresses && savedAddresses.length) {
          const existingAddress = savedAddresses.find((address) => address.area.areaId === areaIdFromUrl);

          if (existingAddress) {
            this.vendorsService.setAddressSearchFilters(existingAddress);
            return { shouldUpdateUrl: false };
          }
        }

        if (area) {
          this.vendorsService.setAddressSearchFilters(this.addressService.createNewAddressFromArea(area));
          return { shouldUpdateUrl: false };
        }

        // The areaID specified in the URL doesn't exist, so we should
        // use the search filters or just remove it from the URL
        return {
          shouldUpdateUrl: true,
          newValue: addressFromSearchFilters ? addressFromSearchFilters.area.areaId : undefined,
        };
      }),
    );
  }
}
